"use client";

import React, { useEffect, useRef, useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { createClient as createAppSupabaseClient } from "@/lib/supabase/client";

interface AuthFormProps {
  type: "login" | "signup";
}

declare global {
  interface Window {
    google?: any;
  }
}

export function AuthForm({ type: initialType }: AuthFormProps) {
  const router = useRouter();

  const supabaseRef = useRef<any | null>(null);
  if (!supabaseRef.current) {
    try {
      supabaseRef.current = createAppSupabaseClient();
    } catch (e) {
      supabaseRef.current = null;
    }
  }
  const supabase = supabaseRef.current;

  const envError = useMemo(() => {
    if (!supabase) {
      return "Supabase client not initialized. Ensure `@/lib/supabase/client` returns a browser client using NEXT_PUBLIC keys.";
    }
    return null;
  }, [supabase]);

  // --- form state ---
  const [mode, setMode] = useState<"login" | "signup">(initialType ?? "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [age, setAge] = useState<number | "">("");
  const [country, setCountry] = useState("");

  // ui state
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);

  // Google button container ref — GSI renders its own button into this div
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const gsiInitializedRef = useRef(false);

  const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID ?? "";

  // password strength helper
  function passwordStrength(pw: string) {
    if (!pw) return { score: 0, label: "Empty" };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const labels = ["Too weak", "Weak", "Okay", "Strong", "Very strong"];
    return { score, label: labels[Math.min(score, labels.length - 1)] };
  }
  const pw = passwordStrength(password);

  // validation
  function validate(): string | null {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address.";
    if (mode === "signup") {
      if (!firstName.trim()) return "Please enter your first name.";
      if (!lastName.trim()) return "Please enter your last name.";
      if (!password || password.length < 8) return "Password must be at least 8 characters.";
      if (pw.score < 2) return "Choose a stronger password (mix letters, numbers, symbols).";
    } else {
      if (!password) return "Please enter your password.";
    }
    return null;
  }

  // redirect helper
  const redirectToDashboardIfSession = async () => {
    if (!supabase) return false;
    try {
      const { data } = await supabase.auth.getSession();
      if (data?.session) {
        router.push("/dashboard");
        router.refresh();
        return true;
      }
    } catch (_) {}
    return false;
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // GSI credential callback — server-verified OTP flow
  // ─────────────────────────────────────────────────────────────────────────────
  const handleGsiCredential = async (response: any) => {
    const idToken = response?.credential;
    if (!idToken) {
      setError("Google sign-in failed (no credential received).");
      setOauthLoading(false);
      return;
    }
    if (!supabase) {
      setError("Supabase client not initialized.");
      setOauthLoading(false);
      return;
    }

    try {
      setError(null);
      setOauthLoading(true);

      // 1. Verify Google ID token server-side → get a Supabase OTP back
      const res = await fetch("/api/auth/google", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });

      const result = await res.json();
      if (!res.ok || result.error) throw new Error(result.error || "Google authentication failed.");

      const { email: googleEmail, token: otp } = result as { email: string; token: string };

      // 2. Exchange OTP for a real Supabase session (no redirect, no email sent)
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: googleEmail,
        token: otp,
        type: "email",
      });

      if (verifyError) throw verifyError;

      // 3. Redirect
      setSuccess("Signed in with Google. Redirecting…");
      const confirmed = await redirectToDashboardIfSession();
      if (!confirmed) {
        router.push("/dashboard");
        router.refresh();
      }
    } catch (err: any) {
      console.error("Google → Supabase sign-in error", err);
      setError(String(err?.message || JSON.stringify(err)));
    } finally {
      setOauthLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // Load GSI script and render the Google button into googleBtnRef.
  //
  // WHY renderButton() instead of prompt():
  //   prompt() uses One Tap which is suppressed (status [5]) by browsers with ITP
  //   or third-party cookie restrictions. renderButton() with ux_mode:"popup"
  //   opens a standard OAuth popup window — completely unaffected by those policies.
  // ─────────────────────────────────────────────────────────────────────────────
  const initGsi = () => {
    if (!GOOGLE_CLIENT_ID || !googleBtnRef.current || gsiInitializedRef.current) return;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGsiCredential,
      ux_mode: "popup", // popup flow — bypasses ITP/third-party cookie restrictions
    });

    // Render an invisible Google button; we proxy clicks from our styled button
    window.google.accounts.id.renderButton(googleBtnRef.current, {
      type: "icon",
      theme: "outline",
      size: "large",
      shape: "circle",
    });

    gsiInitializedRef.current = true;
  };

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;

    const src = "https://accounts.google.com/gsi/client";
    const existing = document.querySelector(`script[src="${src}"]`);

    if (existing) {
      if (window.google?.accounts?.id) initGsi();
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => initGsi();
    script.onerror = () => console.error("Failed to load Google Identity Services script.");
    document.head.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GOOGLE_CLIENT_ID]);

  // Re-init if the ref mounts after the script loaded (e.g. SSR hydration timing)
  useEffect(() => {
    if (window.google?.accounts?.id && googleBtnRef.current && !gsiInitializedRef.current) {
      initGsi();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  // Proxy click: forward our styled button click to the hidden GSI button
  const handleGoogleClick = () => {
    if (!GOOGLE_CLIENT_ID) {
      setError("Missing NEXT_PUBLIC_GOOGLE_CLIENT_ID — configure it in your environment.");
      return;
    }
    const gsiBtn = googleBtnRef.current?.querySelector("div[role=button]") as HTMLElement | null;
    if (gsiBtn) {
      gsiBtn.click();
    } else {
      setError("Google sign-in is still loading. Please try again in a moment.");
    }
  };

  // --- signup ---
  const handleSignup = async () => {
    setError(null);
    setSuccess(null);
    const v = validate();
    if (v) { setError(v); return; }
    if (!supabase) { setError("Supabase client not initialized."); return; }

    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}/dashboard`;
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: redirectTo },
      });

      if (signUpError) throw signUpError;

      if (data?.user && !data?.session) {
        setSuccess("Account created. Please check your email to confirm your address before signing in.");
      } else if (data?.session) {
        setSuccess("Account created and signed in. Redirecting…");
      } else {
        setSuccess("Account created. Please check your email.");
      }

      const userId = data?.user?.id ?? null;
      if (userId) {
        try {
          const { error: insertErr } = await supabase.from("users").insert([{
            id: userId,
            first_name: firstName || null,
            last_name: lastName || null,
            age: age ? Number(age) : null,
            country_of_origin: country || null,
            created_at: new Date().toISOString(),
          }]);
          if (insertErr) console.warn("Profile insert warning:", insertErr);
        } catch (e) {
          console.warn("Profile insert exception:", e);
        }
      }

      const sessionExists = await redirectToDashboardIfSession();
      if (!sessionExists && data?.session) {
        router.push("/dashboard");
        router.refresh();
      }
    } catch (err: any) {
      console.error("signup error", err);
      setError(String(err?.message || err?.error_description || JSON.stringify(err)));
    } finally {
      setLoading(false);
    }
  };

  // --- login ---
  const handleLogin = async () => {
    setError(null);
    setSuccess(null);
    const v = validate();
    if (v) { setError(v); return; }
    if (!supabase) { setError("Supabase client not initialized."); return; }

    setLoading(true);
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
      if (signInError) throw signInError;
      setSuccess("Signed in. Redirecting…");
      const confirmed = await redirectToDashboardIfSession();
      if (!confirmed) { router.push("/dashboard"); router.refresh(); }
    } catch (err: any) {
      console.error("signin error", err);
      setError(String(err?.message || JSON.stringify(err)));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (mode === "signup") await handleSignup();
    else await handleLogin();
  };

  // --- magic link ---
  const handleMagicLink = async () => {
    setError(null);
    setMagicLoading(true);
    if (!supabase) { setError("Supabase client not initialized."); setMagicLoading(false); return; }
    try {
      const redirectTo = `${window.location.origin}/dashboard`;
      const { error: otpErr } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: redirectTo },
      });
      if (otpErr) throw otpErr;
      setSuccess("Magic link sent — check your email.");
    } catch (err: any) {
      console.error("magic link error", err);
      setError(err?.message || "Failed to send magic link.");
    } finally {
      setMagicLoading(false);
    }
  };

  if (envError) {
    return (
      <div className="min-h-screen bg-black text-slate-100 p-8 flex items-center justify-center">
        <div className="max-w-2xl w-full bg-gradient-to-b from-[#070707] to-[#000] border border-yellow-600/8 rounded-2xl p-8">
          <h2 className="text-2xl font-serif font-bold text-[#ffd86b] mb-3">Authentication setup required</h2>
          <p className="text-slate-300 mb-4">{envError}</p>
          <div className="text-sm text-slate-400">
            Ensure your <code className="bg-slate-800 px-1 rounded">/lib/supabase/client</code> returns a browser Supabase client built from{" "}
            <code className="bg-slate-800 px-1 rounded">NEXT_PUBLIC_SUPABASE_URL</code> and{" "}
            <code className="bg-slate-800 px-1 rounded">NEXT_PUBLIC_SUPABASE_ANON_KEY</code>.
          </div>
        </div>
      </div>
    );
  }

  const GoogleSVG = () => (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
      <path fill="#4285F4" d="M43.6 20.5H42V20H24v8h11.3C34 32.9 29.7 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.5-.4-3.5z"/>
      <path fill="#34A853" d="M6.3 14.6l6.6 4.8C14 16 18.6 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6 29.6 4 24 4 16.8 4 10.2 7.9 6.3 14.6z"/>
      <path fill="#FBBC05" d="M24 44c5.5 0 10-1.8 13.3-4.9l-6.1-5c-2 1.5-4.6 2.5-7.2 2.5-5 0-9.4-3.6-10.9-8.4l-6.7 5.2C9.9 39.5 16.5 44 24 44z"/>
      <path fill="#EA4335" d="M43.6 20.5H42V20H24v8h11.3c-1.1 3-3 5.6-5.5 7.6 0 0 0 .1 0 .1 5.3-1.1 9.8-4.5 12.8-8.9 0 0 0-1.9 1.0-6.8z"/>
    </svg>
  );

  return (
    <div className="min-h-screen bg-black text-slate-100 antialiased flex items-center justify-center px-6 py-14">
      <style>{`
        :root { --gold: #ffd86b; --gold-2: #ffb84d; }
        .orb { position: absolute; border-radius: 9999px; filter: blur(32px); opacity: 0.18; pointer-events: none; mix-blend-mode: screen; transform: translate3d(0,0,0); animation: floaty 14s ease-in-out infinite; }
        .orb.l { width: 420px; height: 420px; left: -140px; top: -120px; background: radial-gradient(circle at 20% 35%, rgba(255,200,70,0.14), transparent 30%); }
        .orb.r { width: 280px; height: 280px; right: -60px; top: -10px; background: radial-gradient(circle at 60% 40%, rgba(255,150,40,0.12), transparent 30%); animation-duration: 18s; }
        @keyframes floaty { 0% { transform: translateY(0); } 50% { transform: translateY(-18px); } 100% { transform: translateY(0); } }
        .glass { backdrop-filter: blur(6px); background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.48)); border: 1px solid rgba(255,184,77,0.04); border-radius: 16px; }
        .glow { color: var(--gold); text-shadow: 0 0 12px rgba(255,210,120,0.18), 0 8px 36px rgba(255,160,20,0.04); }
        .card-h { transition: transform .28s, box-shadow .28s; } .card-h:hover { transform: translateY(-6px); box-shadow: 0 22px 60px rgba(255,170,0,0.04); }
        input, textarea { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,184,77,0.04); color: #e6e6e6; }
        input:focus, textarea:focus { outline: none; border-color: var(--gold); box-shadow: 0 8px 36px rgba(255,170,0,0.04); }
        /* GSI renders its button here — hidden visually, we proxy clicks to it */
        .gsi-hidden-btn { position: absolute; opacity: 0; pointer-events: none; width: 1px; height: 1px; overflow: hidden; }
      `}</style>

      <div className="orb l" aria-hidden />
      <div className="orb r" aria-hidden />

      <div className="max-w-5xl w-full grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left panel */}
        <div className="glass p-8 card-h">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-500 to-amber-400 flex items-center justify-center text-black font-bold">TS</div>
            <div>
              <div className="text-xs text-slate-400 uppercase tracking-wide">The Story Club</div>
              <h2 className="text-3xl font-serif font-extrabold glow">{mode === "signup" ? "Join & Create" : "Welcome back"}</h2>
              <p className="text-slate-300 mt-3">A single platform to generate stories, cinematic images, original music and videos — powered by thoughtful AI.</p>
            </div>
          </div>
          <div className="mt-6 space-y-4 text-sm text-slate-300">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-black/40 flex items-center justify-center border border-yellow-600/6 text-[#ffd86b]">★</div>
              <div>
                <div className="font-medium text-slate-100">Fast, high-quality generations</div>
                <div className="text-xs text-slate-400">Premium templates & export options</div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-black/40 flex items-center justify-center border border-yellow-600/6 text-[#ffd86b]">🔒</div>
              <div>
                <div className="font-medium text-slate-100">Safe & ethical</div>
                <div className="text-xs text-slate-400">Privacy-first design & community guidelines</div>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <Image src="/logo.png?height=300&width=400&text=Art+Preview" alt="preview" width={420} height={240} className="rounded-lg border border-yellow-600/6" />
          </div>
        </div>

        {/* Right panel */}
        <div className="glass p-6 card-h">
          <CardHeader className="p-0 mb-4">
            <CardTitle className="text-2xl font-serif font-extrabold glow mb-1">{mode === "signup" ? "Create your account" : "Sign in"}</CardTitle>
            <div className="text-sm text-slate-400">Secure authentication. Also supports Google sign-in and magic links.</div>
          </CardHeader>

          <CardContent className="p-0">
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "signup" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="first" className="text-xs text-slate-300">First name</Label>
                    <Input id="first" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Ada" className="mt-2 py-3" />
                  </div>
                  <div>
                    <Label htmlFor="last" className="text-xs text-slate-300">Last name</Label>
                    <Input id="last" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Lovelace" className="mt-2 py-3" />
                  </div>
                </div>
              )}

              <div>
                <Label htmlFor="email" className="text-xs text-slate-300">Email</Label>
                <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@storyclub.com" className="mt-2 py-3" />
              </div>

              <div className="relative">
                <Label htmlFor="password" className="text-xs text-slate-300">Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="password"
                    type={passwordVisible ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === "signup" ? "Create a secure password" : "Enter your password"}
                    className="py-3 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible((s) => !s)}
                    aria-label={passwordVisible ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300"
                  >
                    {passwordVisible ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {mode === "signup" && (
                  <div className="mt-2 text-xs">
                    <div className="flex items-center justify-between text-slate-400">
                      <div>Password strength: <span className="text-sm text-[#ffd86b]">{pw.label}</span></div>
                      <div className="text-xs">{password.length} chars</div>
                    </div>
                    <div className="mt-1 h-2 w-full bg-slate-800 rounded-full overflow-hidden">
                      <div style={{ width: `${(pw.score / 4) * 100}%` }} className={`h-full ${pw.score >= 3 ? "bg-amber-400" : "bg-yellow-600"}`} />
                    </div>
                  </div>
                )}
              </div>

              {mode === "signup" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="age" className="text-xs text-slate-300">Age</Label>
                    <Input id="age" type="number" value={age === "" ? "" : String(age)} onChange={(e) => setAge(Number(e.target.value) || "")} placeholder="e.g. 28" className="mt-2 py-3" />
                  </div>
                  <div>
                    <Label htmlFor="country" className="text-xs text-slate-300">Country (optional)</Label>
                    <Input id="country" value={country} onChange={(e) => setCountry(e.target.value)} placeholder="United States" className="mt-2 py-3" />
                  </div>
                </div>
              )}

              {(error || success) && (
                <div className={`rounded-md px-3 py-2 text-sm ${error ? "bg-red-900/40 text-red-300" : "bg-yellow-900/10 text-yellow-300"}`}>
                  {error ?? success}
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button type="submit" className="flex-1 rounded-full bg-gradient-to-r from-yellow-500 to-amber-400 text-black py-3" disabled={loading}>
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Processing…</> : mode === "signup" ? "Create account" : "Sign in"}
                </Button>

                {/* Styled Google button — proxies clicks to the hidden GSI button */}
                <div className="relative">
                  {/* GSI renders here (hidden). ux_mode:"popup" bypasses ITP suppression. */}
                  <div ref={googleBtnRef} className="gsi-hidden-btn" aria-hidden="true" />

                  <button
                    type="button"
                    onClick={handleGoogleClick}
                    disabled={oauthLoading}
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-yellow-600/8 bg-black/40 hover:brightness-105 disabled:opacity-50"
                    aria-label="Sign in with Google"
                  >
                    {oauthLoading
                      ? <Loader2 className="w-4 h-4 animate-spin text-slate-300" />
                      : <GoogleSVG />
                    }
                    <span className="text-sm text-slate-200">{oauthLoading ? "Signing in…" : "Google"}</span>
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs text-slate-400 mt-2">
                <button type="button" onClick={handleMagicLink} disabled={magicLoading} className="underline">
                  {magicLoading ? "Sending link…" : "Send magic link"}
                </button>
                <div>
                  {mode === "signup" ? (
                    <span>Already a member?{" "}
                      <button type="button" onClick={() => { setMode("login"); setError(null); setSuccess(null); }} className="underline">Sign in</button>
                    </span>
                  ) : (
                    <span>New here?{" "}
                      <button type="button" onClick={() => { setMode("signup"); setError(null); setSuccess(null); }} className="underline">Create account</button>
                    </span>
                  )}
                </div>
              </div>
            </form>
          </CardContent>

          <div className="mt-4 text-center text-xs text-slate-500">
            By continuing you agree to our <a className="underline" href="/terms">Terms</a> & <a className="underline" href="/privacy">Privacy</a>.
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthForm;
